
import { Component, Vue, namespace } from 'nuxt-property-decorator';
import {
  EventDto,
  ScheduleDto,
  PageType,
  PageScope,
  RoomDto,
  PageDto,
  Dictionary,
  isSuccess,
  LiveStreamType,
} from 'fourwaves-shared';
import groupBy from 'lodash.groupby';
import { parseDateTime } from 'fourwaves-shared/src/helpers';
import { NuxtError } from '@nuxt/types';
import { NoResults, SessionCard, DateCarousel, SessionFilterBar } from 'fourwaves-shared/components';

const EventsModule = namespace('events');

interface SessionRoom {
  session: ScheduleDto;
  room?: RoomDto;
}

@Component({
  scrollToTop: true,
  components: {
    SessionCard,
    DateCarousel,
    NoResults,
    SessionFilterBar,
  },
  validate({ store }) {
    const page = store.state.events.pages.find(({ type }: PageDto) => type === PageType.Schedule);
    if (page && page.scope === PageScope.Participants) {
      if (!store.$auth.loggedIn) {
        const error: NuxtError = Error();
        error.statusCode = 401;
        throw error;
      } else if (!store.getters['events/isParticipant'] && !store.getters['events/isOrganizer']) {
        const error: NuxtError = Error();
        error.statusCode = 403;
        throw error;
      }
    }
    return !!page;
  },
})
export default class SchedulePage extends Vue {
  @EventsModule.State currentEvent!: EventDto;
  @EventsModule.State pages!: PageDto[];
  @EventsModule.Getter isParticipant!: boolean;
  @EventsModule.Getter isOrganizer!: boolean;
  @EventsModule.State currentTime!: string;
  @EventsModule.State sessions!: ScheduleDto[];
  @EventsModule.State rooms!: RoomDto[];
  @EventsModule.Getter liveSessions!: ScheduleDto[];
  @EventsModule.Getter nextRelevantSessionDate!: string | null;
  @EventsModule.Getter isLiveAppOpen!: boolean;
  @EventsModule.Mutation updateSession!: (session: ScheduleDto) => void;

  readonly LiveStreamType = LiveStreamType;

  data: Dictionary<SessionRoom[]> | null = null;
  fallBackModel = {
    date: null,
    isVirtual: false,
    isLive: false,
    isBookmarked: false,
  };

  public head() {
    return {
      title: this.pages.find(({ type }) => type === PageType.Schedule)
        ? this.transl8(this.pages.find(({ type }) => type === PageType.Schedule)!.name)
        : '',
    };
  }

  get currentModel() {
    return { ...this.fallBackModel, ...this.$route.query };
  }

  get sessionDates(): string[] {
    return this.data ? Object.keys(this.data).sort((a, b) => (new Date(b) < new Date(a) ? 1 : -1)) : [];
  }

  get currentDate(): string {
    return this.currentModel.date
      ? this.currentModel.date
      : this.$moment(this.nextRelevantSessionDate).format('YYYY-MM-DD');
  }

  get daySessions(): SessionRoom[] {
    return this.sessions
      .filter(session => parseDateTime(session.startDate).format('YYYY-MM-DD') === this.currentDate)
      .map(session => ({
        session,
        room: this.rooms!.find(room => room.id === session.roomId),
      }));
  }

  get filteredDaySessions(): SessionRoom[] {
    return this.daySessions.filter(this.filterByBookmark()).filter(this.filterByLive()).filter(this.filterByVirtual());
  }

  created() {
    const schedules = this.sessions.map(session => ({
      session,
      room: this.rooms!.find(room => room.id === session.roomId),
    }));
    this.data = groupBy(schedules, item => parseDateTime(item.session.startDate).format('YYYY-MM-DD'));
  }

  public filterByBookmark() {
    return ({ session }) => {
      return !this.currentModel.isBookmarked ? true : session.isBookmarkedByCurrentUser;
    };
  }

  public filterByLive() {
    return ({ session }) => {
      return !this.currentModel.isLive ? true : this.liveSessions.some(({ id }) => id === session.id);
    };
  }

  public filterByVirtual() {
    return sessionRoom => {
      return !this.currentModel.isVirtual
        ? true
        : sessionRoom.room && sessionRoom.room.liveStreamType !== LiveStreamType.None;
    };
  }

  public async toggleBookmark(session: ScheduleDto) {
    if (!this.sessions) return;

    const sessionItem = this.sessions.find(({ id }) => id === session.id);
    if (!sessionItem) return;

    if (sessionItem.isBookmarkedByCurrentUser) {
      const result = await this.$api.unbookmarkSession(sessionItem.id);
      if (isSuccess(result)) {
        this.updateSession({ ...sessionItem, isBookmarkedByCurrentUser: false });
      }
    } else {
      const result = await this.$api.bookmarkSession(sessionItem.id);
      if (isSuccess(result)) {
        this.updateSession({ ...sessionItem, isBookmarkedByCurrentUser: true });
      }
    }
  }
}
